import React, { useState } from "react";
import { useDropzone } from "react-dropzone";
import mammoth from "mammoth";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  CircularProgress,
} from "@mui/material";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import {
  faCirclePlus,
  faCloudUploadAlt,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Toggle from "common/Toggle";
import toast from "react-hot-toast";
import { useNavigate } from "react-router";
import { SubpagesCreate } from "services/apiService";

const SubPagesCreate = () => {
  const [subpagesForm, setSubpagesForm] = useState({
    page_name: "",
    description: "",
    is_active: 0,
  });

  const [errors, setErrors] = useState({ page_name: "", description: "" });
  const [fileName, setFileName] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const navigate = useNavigate();

  const handleTextFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setSubpagesForm((prev) => ({ ...prev, [id]: value }));
  };

  const handleToggleChange = (checked: number, field: string) => {
    setSubpagesForm((prevData) => ({ ...prevData, [field]: checked ? 1 : 0 }));
  };

  const validateForm = () => {
    const newErrors = { page_name: "", description: "" };
    let isValid = true;

    if (!subpagesForm.page_name.trim()) {
      newErrors.page_name = "Page name is required.";
      isValid = false;
    }

    if (!subpagesForm.description.trim()) {
      newErrors.description = "Description is required.";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      try {
        const payload = {
          page_name: subpagesForm.page_name,
          page_content: subpagesForm.description,
          is_active: subpagesForm.is_active,
        };

        const response = await SubpagesCreate(payload);
        if (response.status_code === 200) {
          toast.success("SubPage created successfully");
          navigate("/sub-pages-list");
        } else {
          toast.error("SubPage creation failed");
        }
      } catch (error) {
        toast.error("SubPage creation failed");
      }
    }
  };

  const onDrop = (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      setIsUploading(true);
      const reader = new FileReader();

      reader.onload = async (event) => {
        const arrayBuffer = event.target?.result as ArrayBuffer;
        if (arrayBuffer) {
          setFileName(file.name);
          try {
            const { value: htmlContent } = await mammoth.convertToHtml({
              arrayBuffer,
            });

            // Ensure extracted content is rich text and retains formatting
            setSubpagesForm((prev) => ({
              ...prev,
              description: htmlContent,
            }));
          } catch (error) {
            console.error("DOCX parsing error:", error);
            toast.error("Failed to extract content from the file.");
          } finally {
            setIsUploading(false);
          }
        }
      };

      reader.readAsArrayBuffer(file);
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: false,
  });

  return (
    <form onSubmit={handleSubmit}>
      <Box sx={{ padding: 4 }}>
        <Box display="flex" flexDirection="column" gap={3}>
          <TextField
            variant="filled"
            id="page_name"
            label="Title"
            fullWidth
            value={subpagesForm.page_name}
            onChange={handleTextFieldChange}
            error={!!errors.page_name}
            helperText={errors.page_name}
          />
          <CKEditor
            editor={ClassicEditor}
            data={subpagesForm.description}
            config={{
              extraPlugins: [
                /* Add any required custom plugins here */
              ],
              htmlSupport: {
                allow: [
                  {
                    name: /.*/, // Allow all HTML elements
                    attributes: true,
                    classes: true,
                    styles: true,
                  },
                ],
              },
            }}
            onChange={(event, editor) => {
              const data = editor.getData();
              setSubpagesForm((prev) => ({
                ...prev,
                description: data, // Store full HTML content
              }));
            }}
          />

          <Paper
            {...getRootProps()}
            sx={{
              padding: 3,
              border: "2px dashed #1976d2",
              textAlign: "center",
              cursor: "pointer",
            }}
          >
            <input {...getInputProps()} />
            <FontAwesomeIcon
              icon={faCloudUploadAlt}
              style={{ fontSize: "40px", color: "#1976d2" }}
            />
            {isUploading ? (
              <CircularProgress
                size={24}
                sx={{ color: "#1976d2", marginTop: 1 }}
              />
            ) : (
              "Drag & Drop your Word document here or click to select a file"
            )}
            {fileName && (
              <Typography sx={{ color: "#1976d2", marginTop: 1 }}>
                {fileName}
              </Typography>
            )}
          </Paper>
          <Toggle
            checked={subpagesForm.is_active === 1}
            onChange={(checked) => handleToggleChange(checked, "is_active")}
          />
          <Button
            variant="contained"
            color="primary"
            size="large"
            type="submit"
            disabled={!(subpagesForm.page_name && subpagesForm.description)}
          >
            <FontAwesomeIcon icon={faCirclePlus} /> &nbsp; Create Page
          </Button>
        </Box>
      </Box>
    </form>
  );
};

export default SubPagesCreate;
